import { useEffect, useState } from "react";
import { calculationsApi, type SavedCalculation } from "../api/client";
import { useAuth } from "../auth/auth-context";
import { useNavigation } from "../router-context";
import { formatMxn } from "../engine/money";

export function SavedCalculationsPage() {
  const { user, enabled, loading: authLoading, signIn, signOut } = useAuth();
  const accessToken = user?.access_token;
  const { navigate } = useNavigation();
  const [items, setItems] = useState<SavedCalculation[] | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!accessToken) return;
    let active = true;
    void calculationsApi
      .list(accessToken)
      .then((response) => {
        if (active) setItems(response.items);
      })
      .catch((caught: unknown) => {
        if (active) {
          setError(
            caught instanceof Error ? caught.message : "No fue posible cargar.",
          );
          setItems([]);
        }
      });
    return () => {
      active = false;
    };
  }, [accessToken]);

  const remove = async (id: string) => {
    if (!user || !window.confirm("¿Eliminar este cálculo de forma permanente?")) return;
    await calculationsApi.remove(user.access_token, id);
    setItems((current) => current?.filter((item) => item.id !== id) ?? []);
  };

  const duplicate = (item: SavedCalculation) => {
    sessionStorage.setItem("legalflow:duplicate", JSON.stringify(item.input));
    navigate("/");
  };

  const exportAll = () => {
    const blob = new Blob([JSON.stringify(items ?? [], null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `legalflow-calculos-${new Date().toISOString().slice(0, 10)}.json`;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const deleteAccount = async () => {
    if (
      !user ||
      !window.confirm(
        "Se eliminarán tu cuenta y todos tus cálculos. Esta acción no se puede deshacer. ¿Continuar?",
      )
    ) {
      return;
    }
    await calculationsApi.deleteAccount(user.access_token);
    await signOut();
  };

  if (authLoading) {
    return <div className="empty-state">Verificando tu cuenta…</div>;
  }
  if (!user) {
    return (
      <section className="empty-state">
        <span className="eyebrow">Persistencia opcional</span>
        <h1>Guarda y revisa tus cálculos</h1>
        <p>
          La calculadora funciona sin registro. Crea una cuenta únicamente si
          quieres conservar, duplicar, exportar o eliminar resultados.
        </p>
        <button
          className="button button--gold"
          disabled={!enabled}
          onClick={() => void signIn()}
        >
          Crear cuenta o iniciar sesión
        </button>
        {!enabled && (
          <small>La cuenta se habilitará al publicar la infraestructura AWS.</small>
        )}
      </section>
    );
  }

  return (
    <section className="saved-page">
      <header className="page-heading">
        <div>
          <span className="eyebrow">Tu espacio privado</span>
          <h1>Mis cálculos</h1>
          <p>Todos los registros están aislados por tu identidad de AWS Cognito.</p>
        </div>
        <button
          className="button button--outline"
          onClick={exportAll}
          disabled={!items?.length}
        >
          Exportar JSON
        </button>
      </header>

      {error && <div className="form-error">{error}</div>}
      {items === null ? (
        <div className="empty-state">Cargando cálculos…</div>
      ) : items.length === 0 ? (
        <div className="empty-state">
          <h2>Aún no guardas cálculos</h2>
          <p>Realiza uno y elige “Guardar cálculo” en el resultado.</p>
          <button className="button button--dark" onClick={() => navigate("/")}>
            Ir a la calculadora
          </button>
        </div>
      ) : (
        <div className="saved-grid">
          {items.map((item) => (
            <article className="saved-card" key={item.id}>
              <span className="saved-card__kind">
                {item.kind === "real-estate-isr"
                  ? "Fiscal · ISR inmobiliario"
                  : `Laboral · ${item.kind}`}
              </span>
              <h2>{item.title}</h2>
              <strong className="saved-card__total">
                {formatMxn(
                  item.kind === "real-estate-isr"
                    ? item.result.totals.isr
                    : item.result.totals.net,
                )}
              </strong>
              <p>
                {new Intl.DateTimeFormat("es-MX", {
                  dateStyle: "medium",
                  timeStyle: "short",
                }).format(new Date(item.createdAt))}
              </p>
              <small>{item.result.rulesVersion}</small>
              <div className="saved-card__actions">
                <button
                  className="button button--outline button--small"
                  onClick={() => duplicate(item)}
                >
                  Duplicar
                </button>
                <button
                  className="button button--danger button--small"
                  onClick={() => void remove(item.id)}
                >
                  Eliminar
                </button>
              </div>
            </article>
          ))}
        </div>
      )}

      <section className="account-danger">
        <div>
          <h2>Eliminar cuenta</h2>
          <p>Suprime tu cuenta y todos los cálculos activos asociados.</p>
        </div>
        <button className="button button--danger" onClick={() => void deleteAccount()}>
          Eliminar mi cuenta
        </button>
      </section>
    </section>
  );
}
